import React from "react";

import { DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  EntityErrors,
  INVOLVEMENT_OPTIONS,
  requiresIdentity,
  requiresInvolvement,
} from "@/components/alerts/add-v2/model/entityRules";
import {
  BulletinEntity,
  EntityType,
} from "@/components/alerts/add-v2/model/types";

import { ENTITY_FIELD_GRID_SX, ENTITY_FULL_ROW_SX } from "./entityFieldLayout";
import {
  ENTITY_TYPE_ICONS,
  ENTITY_TYPE_ORDER,
  entityChipLabel,
} from "./entityTypeMeta";
import { ItemFields } from "./ItemFields";
import { PersonFields } from "./PersonFields";
import { VehicleFields } from "./VehicleFields";

/**
 * The expanded entity — the inline form the design asks for: no modal, no drawer, no side panel.
 *
 * **It writes straight through.** Every keystroke goes into `values.entities`, exactly like
 * `ContactRow`, `LocationRow` and `IncidentTimeRow` do, and autosave picks it up from there. There
 * is no Save button and no staging buffer: the earlier build had both, and they were the only place
 * in this directory where a field did not reach the document immediately.
 *
 * What that costs, stated so nobody looks for them: **there is no Cancel** — the only undo is
 * Delete. The design's "required-field errors appear only after a save attempt" had nothing left to
 * hang on, so what replaced it is the rule the other row lists use: a row says what is missing once
 * it holds something. `EntitiesSection` decides that; this component only renders what it is given.
 *
 * `Done` only collapses the card. It is not a commit, and an entity that is still completely empty
 * when it collapses is removed rather than left in the document.
 */

interface EntityEditorProps {
  entity: BulletinEntity;
  /** 1-based within its own type, for the heading. Derived at render time, never stored. */
  index: number;
  bulletinType: string | null;
  /** `validateEntityDraft`'s output — the same call `validation/schemas.ts` makes. */
  errors: EntityErrors;
  onChange: (next: BulletinEntity) => void;
  onChangeType: (type: EntityType) => void;
  onDone: () => void;
  onDelete: () => void;
}

export const EntityEditor: React.FC<EntityEditorProps> = ({
  entity,
  index,
  bulletinType,
  errors,
  onChange,
  onChangeType,
  onDone,
  onDelete,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const label = entityChipLabel(entity, index, t);

  const message = (code: string | undefined) =>
    code ? t(`validation.entity_${code}`) : undefined;

  const patch = (next: Partial<BulletinEntity>) =>
    onChange({ ...entity, ...next } as BulletinEntity);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 500 }}>
            {label}
          </Typography>
          <IconButton
            size="small"
            aria-label={t("entities.delete_entity", { label })}
            onClick={onDelete}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Box>

        {/*
          The type stays switchable for the life of the row. Switching rebuilds the entity through
          `createEntity` (decision 10), so **everything already entered is cleared** — that is the
          point, not a side effect: a person's date of birth has no meaning on a vehicle, and the
          alternative is a row carrying fields nothing on screen shows.
        */}
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={entity.type}
          aria-label={t("entities.editor_type")}
          onChange={(_event, next: EntityType | null) => {
            if (next) onChangeType(next);
          }}
        >
          {ENTITY_TYPE_ORDER.map((type) => (
            <ToggleButton
              key={type}
              value={type}
              sx={{ gap: 1, textTransform: "none" }}
            >
              {ENTITY_TYPE_ICONS[type]}
              {t(`entities.${type}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={ENTITY_FIELD_GRID_SX}>
          <TextField
            select
            required={requiresInvolvement(entity.type)}
            size="small"
            fullWidth
            error={!!errors.involvement}
            helperText={message(errors.involvement)}
            label={t("entities.involvement")}
            value={entity.involvement ?? ""}
            onChange={(event) =>
              patch({
                involvement: (event.target.value ||
                  null) as BulletinEntity["involvement"],
              })
            }
          >
            {/*
              Only where involvement is optional, which today is Item / Other. Without it a
              mis-picked involvement cannot be taken back off — and "no role to state" is the
              honest answer on most items, so it has to stay reachable. The empty value maps to
              `null` in the handler above, so nothing is stored for it.
            */}
            {!requiresInvolvement(entity.type) && (
              <MenuItem value="">
                <em>{t("entities.involvement_none")}</em>
              </MenuItem>
            )}
            {INVOLVEMENT_OPTIONS[entity.type].map((option) => (
              <MenuItem key={option} value={option}>
                {t(`entities.involvement_${option}`)}
              </MenuItem>
            ))}
          </TextField>

          {entity.involvement === "other" && (
            <TextField
              size="small"
              fullWidth
              required
              error={!!errors.otherInvolvement}
              helperText={message(errors.otherInvolvement)}
              label={t("entities.other_involvement")}
              value={entity.otherInvolvement}
              onChange={(event) =>
                patch({ otherInvolvement: event.target.value })
              }
            />
          )}

          {entity.type === "person" && (
            <PersonFields
              value={entity}
              identityRequired={requiresIdentity(bulletinType)}
              errors={errors}
              onPatch={patch}
            />
          )}
          {entity.type === "vehicle" && (
            <VehicleFields value={entity} onPatch={patch} />
          )}
          {entity.type === "item" && (
            <ItemFields value={entity} errors={errors} onPatch={patch} />
          )}
        </Box>

        <Box sx={{ display: "flex", ...ENTITY_FULL_ROW_SX }}>
          <Button variant="outlined" onClick={onDone}>
            {t("entities.done")}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
